import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useAuthContext } from '../../context/auth-context';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { useZoneCatalog } from '../../hooks/use-zone-catalog';
import { useUpdateUserRoleMutation } from '../../redux/services';
import {
  ROLES,
  ROLES_REQUIRING_ZONE,
  assignableRoles,
  isAdministrativeRole,
  roleLabel,
} from '../../constants/roles';

/**
 * Cambia el rol de un usuario y, si el rol lo exige, su zona.
 *
 * El rol y la zona se mandan en la misma petición porque el servidor los trata como
 * una unidad: rechaza un `sec` sin zona y borra la zona al dejar de serlo. Un
 * formulario que los separase permitiría guardar un Secretario de Zona sin zona, que
 * es el estado que vacía todos sus reportes en silencio.
 */
export const RoleDialog = ({ open, user, onClose }) => {
  const { user: actor, hasPermission, activeCamp } = useAuthContext();
  const isMobile = useIsMobile();
  const [updateUserRole, { isLoading }] = useUpdateUserRoleMutation();

  const [role, setRole] = useState(user?.role || ROLES.BASIC);
  const [zone, setZone] = useState(user?.zone || '');
  const [error, setError] = useState(null);

  const needsZone = ROLES_REQUIRING_ZONE.includes(role);
  // Solo las zonas del campamento activo: se administra un campamento a la vez, y una
  // zona de otro dejaría al secretario sin clubes que ver. Se piden cuando hacen falta.
  const { zones, isFetching: loadingZones } = useZoneCatalog({
    campId: activeCamp?._id,
    skip: !open || !needsZone,
  });

  // El diálogo se monta una sola vez y se reutiliza para cada fila, así que el estado
  // hay que resembrarlo con el usuario que llega.
  useEffect(() => {
    if (!open) return;
    setRole(user?.role || ROLES.BASIC);
    setZone(user?.zone || '');
    setError(null);
  }, [open, user]);

  /**
   * El rol actual va en la lista aunque no sea asignable (un `super-admin`, un
   * `director`, o un `admin` mirado por otro `admin`): si no, el Select arrancaría con
   * un valor fuera de rango y MUI lo pintaría vacío, como si no tuviera rol. Va
   * `disabled`, así que se ve lo que la cuenta ES sin ofrecerlo como destino.
   */
  const options = useMemo(() => {
    const assignable = assignableRoles(hasPermission);
    return [...new Set([user?.role, ...assignable].filter(Boolean))].map(value => ({
      value,
      asignable: assignable.includes(value),
    }));
  }, [hasPermission, user?.role]);

  /**
   * La zona actual va en la lista aunque no sea de este campamento. El servidor acepta
   * cualquier zona de la organización, así que ese estado existe —un secretario movido
   * de campamento, o asignado antes de acotar el listado—, y si el Select no la
   * conociera lo pintaría vacío: parecería que la cuenta se quedó sin zona.
   */
  const zoneOptions = useMemo(() => {
    const actual = user?.zone;
    return actual && !zones.includes(actual) ? [actual, ...zones] : zones;
  }, [zones, user?.zone]);

  const sinCambios = role === (user?.role || '') && (!needsZone || zone === (user?.zone || ''));
  const puedeGuardar = !isLoading && !sinCambios && (!needsZone || Boolean(zone));

  const handleSubmit = async () => {
    setError(null);
    try {
      await updateUserRole({ id: user._id, role, zone: needsZone ? zone : undefined }).unwrap();
      onClose();
    } catch (err) {
      setError(err?.data?.message || 'No se pudo cambiar el rol.');
    }
  };

  const promoviendo = isAdministrativeRole(role) && !isAdministrativeRole(user?.role);
  const perdiendoZona = user?.role === ROLES.SEC && role !== ROLES.SEC;
  // Degradarse a uno mismo es la vía más rápida de perder el acceso a esta pantalla:
  // la identidad se revalida contra la BD y el rol nuevo entra en ≤60 s, sin re-login.
  const autoDegradacion =
    actor?._id === user?._id && isAdministrativeRole(user?.role) && !isAdministrativeRole(role);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Cambiar rol</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{user?.fullname}</strong> (@{user?.username}) — hoy es{' '}
            {roleLabel(user?.role)}
            {user?.zone ? ` · ${user.zone}` : ''}.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small">
            <InputLabel id="role-select">Rol</InputLabel>
            <Select
              labelId="role-select"
              label="Rol"
              value={role}
              onChange={e => setRole(e.target.value)}
            >
              {options.map(({ value, asignable }) => (
                <MenuItem key={value} value={value} disabled={!asignable}>
                  {roleLabel(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsZone && (
            <FormControl fullWidth size="small" error={!zone}>
              <InputLabel id="zone-select">Zona</InputLabel>
              <Select
                labelId="zone-select"
                label="Zona"
                value={zoneOptions.includes(zone) ? zone : ''}
                onChange={e => setZone(e.target.value)}
                disabled={loadingZones}
              >
                {zoneOptions.map(z => (
                  <MenuItem key={z} value={z}>
                    {z}
                    {zones.includes(z) ? '' : ' · de otro campamento'}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {loadingZones
                  ? 'Cargando zonas…'
                  : zones.length === 0
                    ? 'Este campamento no tiene zonas. Créalas en Configuración antes de asignar este rol.'
                    : 'Define qué clubes y reportes verá. Es obligatoria para este rol.'}
              </FormHelperText>
            </FormControl>
          )}

          {promoviendo && (
            <Alert severity="warning">
              {roleLabel(role)} es una cuenta administrativa: podrá gestionar la
              aplicación entera, sin límite de zona.
            </Alert>
          )}

          {perdiendoZona && (
            <Alert severity="info">
              Al dejar de ser Secretario de Zona se le quitará la zona «{user?.zone}».
            </Alert>
          )}

          {autoDegradacion && (
            <Alert severity="error">
              Es tu propia cuenta. Al guardar perderás el acceso a esta pantalla y
              necesitarás que otra persona te devuelva el rol.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!puedeGuardar}>
          {isLoading ? <CircularProgress size={20} /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDialog;
